import type { RouteWithTitle } from '@pbk-web/types';
import { Roles } from '@pbk-web/types';
import { lazy } from 'react';

const workspaceRoutes: RouteWithTitle[] = [
  {
    id: 'client-dashboard',
    path: '/clients/:clientId/dashboard',
    translationKey: 'page.client-dashboard',
    permissions: [Roles.Management],
    component: lazy(() => import('../pages/clients/Dashboard')),
  },
  {
    id: 'client-home',
    breadcrumb: 'Home',
    path: '/clients/:clientId/home',
    translationKey: 'page.client-home',
    component: lazy(() => import('../pages/home/HomePage')),
  },
  {
    id: 'client-modules',
    breadcrumb: 'Modules',
    path: '/clients/:clientId/modules',
    translationKey: 'page.client-modules',
    component: lazy(() => import('../pages/module/Modules')),
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
  },
  {
    id: 'module-details',
    path: '/clients/:clientId/modules/:moduleId',
    translationKey: 'page.module-details',
    component: lazy(() => import('../pages/module/ModuleV2')),
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
  },
  {
    id: 'client-assets',
    path: '/clients/:clientId/assets',
    translationKey: 'page.client-assets',
    component: lazy(() => import('../pages/assets/Assets')),
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
  },
  {
    id: 'form-redirect',
    path: '/clients/:clientId/forms/:formId/:version/redirect',
    translationKey: 'page.form-page',
    component: lazy(() => import('../pages/form/FormRedirect')),
  },
  {
    id: 'form-page',
    path: '/clients/:clientId/forms/:formId',
    translationKey: 'page.form-page',
    // Id-polymorphic switch: 26-char ULID -> V2 viewer (behind playbook-forms-v2 PostHog flag),
    // GUID -> legacy V1 FormPage (unchanged).
    component: lazy(() => import('../pages/form/FormRouteSwitch')),
    // NOTE: ExternalAuditor isn't allowed to view the form, but we only decide
    // to redirect to preview ON this page, so allowing through here
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
  },
  {
    id: 'form-preview',
    path: '/clients/:clientId/forms/:formId/preview',
    translationKey: 'page.form-preview',
    // Id-polymorphic switch: 26-char ULID -> V2 viewer in read-only preview mode (behind playbook-forms-v2
    // PostHog flag), GUID -> legacy V1 FormPreview (unchanged). Keeps existing V1 preview links working.
    component: lazy(() => import('../pages/form/FormPreviewRouteSwitch')),
    permissions: [Roles.Employee, Roles.ExternalAuditor, Roles.ExternalContributor],
  },
  {
    // Reduced "test instance" viewer for a V2 template's preview instance. Opened by the builder's
    // Preview button (new tab) or by direct URL; ULID-only, behind the playbook-forms-v2 flag.
    id: 'form-test',
    path: '/clients/:clientId/forms/:formId/test',
    translationKey: 'page.form-page',
    component: lazy(() => import('../pages/form/FormTestRouteSwitch')),
    permissions: [Roles.Employee, Roles.ExternalContributor],
  },
  // V2 demo routes, gated on the `playbook-forms-v2` PostHog flag (its evaluation context keeps it
  // off in prod). `forms-v2-live` creates real instances, so keep its flag scoped to non-prod.
  // The V2 panels demo lives in its own lib now (`formsV2BuilderRoutes`); these two stay here
  // because the viewer lib is lazy-loaded and so cannot be statically imported by the router.
  {
    // V2 action-render demo. Reachable at /clients/:clientId/forms-v2-actions-demo.
    // Wrapped so the gallery gets the real Attachment file-service adapter.
    id: 'forms-v2-actions-demo',
    path: '/clients/:clientId/forms-v2-actions-demo',
    translationKey: 'page.form-page',
    component: lazy(() => import('../components/forms-v2/FormsV2ActionDemoRoute')),
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
    postHogFlag: 'playbook-forms-v2',
  },
  {
    // V2 dynamic-data (`${{…}}`) explorer on mock bindings. Reachable at /clients/:clientId/forms-v2-dynamic-data-demo.
    id: 'forms-v2-dynamic-data-demo',
    path: '/clients/:clientId/forms-v2-dynamic-data-demo',
    translationKey: 'page.form-page',
    component: lazy(() => import('../components/forms-v2/FormsV2DynamicDataDemoRoute')),
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
    postHogFlag: 'playbook-forms-v2',
  },
  {
    // Launcher: lists/creates real instances and opens them in the real V2 viewer
    // at /clients/:clientId/forms/:id. Reachable at /clients/:clientId/forms-v2-live.
    id: 'forms-v2-live',
    path: '/clients/:clientId/forms-v2-live',
    translationKey: 'page.form-page',
    // Wrapped so the launcher's "New file-based document" flow gets the real file-service adapter.
    component: lazy(() => import('../components/forms-v2/FormsV2LiveRoute')),
    permissions: [Roles.Employee, Roles.ExternalContributor, Roles.ExternalAuditor],
    postHogFlag: 'playbook-forms-v2',
  },
  {
    id: 'risk-overview',
    path: '/clients/:clientId/risk',
    translationKey: 'page.risk-overview',
    component: lazy(() => import('../pages/risk/Overview')),
  },
  {
    id: 'my-org',
    path: '/clients/:clientId/organisation',
    translationKey: 'page.my-org',
    component: lazy(() => import('../pages/my-org/MyOrg')),
    permissions: [Roles.TeamLead, Roles.TeamMember],
    canOverridePermissions: true,
  },
  {
    id: 'org-edit-contact',
    path: '/clients/:clientId/contacts/:contactId',
    translationKey: 'page.org-contact-edit',
    component: lazy(() => import('../pages/my-org/ContactEditPage')),
    permissions: [Roles.TeamLead, Roles.TeamMember],
  },
  {
    id: 'client-form-builder',
    path: [
      '/clients/:clientId/form-builder/:templateFormId/:templateFormVersion',
      '/clients/:clientId/form-builder/:templateFormId',
      '/clients/:clientId/form-builder',
    ],
    translationKey: 'page.form-builder',
    permissions: [Roles.TeamLead],
    component: lazy(() => import('../pages/admin/FormBuilder')),
    canOverridePermissions: true,
  },
  {
    id: 'client-module-builder',
    path: ['/clients/:clientId/module-builder/:templateModuleId/sections', '/clients/:clientId/module-builder/:templateModuleId/forms/:sectionId'],
    translationKey: 'page.module-builder',
    permissions: [Roles.TeamLead],
    component: lazy(() => import('../pages/admin/ModuleBuilder')),
  },
  {
    id: 'client-resource-builder',
    path: [
      '/clients/:clientId/resource-builder/:templateFormId/:templateFormVersion',
      '/clients/:clientId/resource-builder/:templateFormId',
      '/clients/:clientId/resource-builder',
    ],
    translationKey: 'page.resource-builder',
    permissions: [Roles.TeamLead],
    component: lazy(() => import('../pages/admin/ResourceBuilder')),
  },
  {
    id: 'client-picklist-builder',
    path: '/clients/:clientId/picklists/:picklistId',
    translationKey: 'page.picklist-details',
    permissions: [Roles.TeamLead],
    component: lazy(() => import('../pages/admin/picklist-builder/PicklistDetails')),
  },
  {
    id: 'notifications',
    path: ['/clients/:clientId/notifications', '/notifications'],
    translationKey: 'page.notifications',
    component: lazy(() => import('../pages/Notifications')),
  },
  {
    id: 'user-settings',
    path: ['/clients/:clientId/user-settings', '/user-settings'],
    translationKey: 'page.user-settings',
    component: lazy(() => import('../pages/user/Settings')),
  },
  {
    id: 'resources',
    breadcrumb: 'Resources',
    path: '/clients/:clientId/resources',
    translationKey: 'page.client-resources',
    component: lazy(() => import('../pages/resources/Resources')),
    permissions: [Roles.Employee],
  },
  {
    id: 'resource',
    path: '/clients/:clientId/resources/:resourceId',
    translationKey: 'page.client-resources',
    component: lazy(() => import('../pages/resources/Resource')),
    permissions: [Roles.Employee],
  },
  {
    id: 'resource-edit',
    path: '/clients/:clientId/resources/:resourceId/:recordId',
    translationKey: 'page.record-edit',
    component: lazy(() => import('../pages/resources/RecordEditPage')),
    permissions: [Roles.Employee],
  },
  {
    id: 'documents',
    breadcrumb: 'Document Space',
    path: '/clients/:clientId/documents',
    translationKey: 'page.documents',
    component: lazy(() => import('../pages/documents/Documents')),
  },
];

export default workspaceRoutes;
